// Insurance Training Scenarios
// Generic scenarios for sales practice with various client personas

export interface ClientPersona {
  id: string;
  name: string;
  nameKo: string;
  age: number;
  gender: 'male' | 'female';
  occupation: string;
  occupationKo: string;
  family: {
    maritalStatus: string;
    maritalStatusKo: string;
    children: number;
    childrenAges: number[];
  };
  financial: {
    businessType?: string;
    businessTypeKo?: string;
    annualIncome?: string;
    annualIncomeKo?: string;
    assets?: string;
    assetsKo?: string;
  };
  existingCoverage: {
    type: string;
    typeKo: string;
    provider: string;
    providerKo: string;
    coverage: string;
    coverageKo: string;
  }[];
  concerns: string[];
  concernsKo: string[];
  goals: string[];
  goalsKo: string[];
  personality: string;
  personalityKo: string;
  initialMessage: string;
  initialMessageKo: string;
  avatarUrl: string;
  voiceId: string; // ElevenLabs voice ID
}

export interface InsuranceScenario {
  id: string;
  code: string;
  title: string;
  titleKo: string;
  description: string;
  descriptionKo: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number; // minutes
  learningObjectives: string[];
  learningObjectivesKo: string[];
  clientPersona: ClientPersona;
  agentId: string; // ElevenLabs agent ID
  language: string;
  successCriteria: string[];
  successCriteriaKo: string[];
}

// Shared Persona Configurations (for reuse across scenarios)
const sharedPersonas: Record<string, ClientPersona> = {
  'lee-sangho-001': {
    id: 'lee-sangho-001',
    name: 'Lee Sang-ho',
    nameKo: '이상호',
    age: 45,
    gender: 'male',
    occupation: 'Logistics Company Owner',
    occupationKo: '물류 회사 대표',
    family: {
      maritalStatus: 'Married',
      maritalStatusKo: '기혼',
      children: 2,
      childrenAges: [10, 13],
    },
    financial: {
      businessType: 'Mid-sized logistics company (50 employees)',
      businessTypeKo: '중견 물류 회사 (직원 50명)',
      annualIncome: '500M - 1B KRW',
      annualIncomeKo: '연 5억 - 10억원',
      assets: '5B - 10B KRW',
      assetsKo: '50억 - 100억원',
    },
    existingCoverage: [
      {
        type: 'Term Life Insurance',
        typeKo: '정기생명보험',
        provider: 'Another company',
        providerKo: '타사',
        coverage: '500M KRW',
        coverageKo: '5억원',
      },
      {
        type: 'Basic Medical Insurance',
        typeKo: '실손의료보험',
        provider: 'National Health Insurance',
        providerKo: '국민건강보험',
        coverage: 'Basic coverage',
        coverageKo: '기본 보장',
      },
    ],
    concerns: [
      'inheritance_tax',
      'family_lifestyle_protection',
      'business_continuity',
      'children_education',
      'retirement_planning',
    ],
    concernsKo: [
      '상속세 부담',
      '가족 생활 수준 보호',
      '사업 연속성',
      '자녀 교육',
      '은퇴 준비',
    ],
    goals: [
      'Protect family lifestyle if something happens',
      'Minimize inheritance tax burden',
      'Ensure business continuity',
      'Secure children education fund',
      'Plan for comfortable retirement',
    ],
    goalsKo: [
      '만약의 사태 시 가족 생활 수준 보호',
      '상속세 부담 최소화',
      '사업 연속성 보장',
      '자녀 교육비 확보',
      '안정적인 은퇴 준비',
    ],
    personality:
      'Practical, business-minded, values family highly, concerned about legacy, somewhat risk-averse, prefers concrete numbers and guarantees',
    personalityKo:
      '실용적, 사업가적 사고, 가족 중시, 유산에 대한 관심, 다소 보수적, 구체적 수치와 보장 선호',
    initialMessage:
      "Hello, I'm Lee Sang-ho. I wanted to get some advice about insurance.",
    initialMessageKo:
      '안녕하세요, 이상호입니다. 보험 상담을 받고 싶어서 연락드렸습니다.',
    avatarUrl:
      'https://api.heygen.com/v1/streaming.new?avatar_id=Anna_public_3_20240108',
    voiceId: 'Ori1rnHIeeysIxrsFZ2X', // ElevenLabs Korean male voice
  },
};

// Insurance Scenarios Database
export const insuranceScenarios: Record<string, InsuranceScenario> = {
  'aia-1': {
    id: 'aia-korea-001',
    code: 'aia-1',
    title: 'Affluent Client Needs Discovery - Business Owner',
    titleKo: '고액 자산가 니즈 발굴 - 사업주',
    description:
      'Practice conducting needs discovery with an affluent business owner concerned about family protection and inheritance tax planning',
    descriptionKo:
      '가족 보호 및 상속세 설계에 관심 있는 고액 자산가 사업주와의 니즈 발굴 연습',
    difficulty: 'intermediate',
    estimatedDuration: 15,
    learningObjectives: [
      'Conduct effective needs discovery with affluent clients',
      'Identify wealth transfer and inheritance tax planning needs',
      'Recommend appropriate products based on client profile',
      'Address concerns about business continuity and family protection',
      'Build rapport with high-net-worth individuals',
    ],
    learningObjectivesKo: [
      '고액 자산가와 효과적인 니즈 발굴 수행',
      '자산 승계 및 상속세 설계 니즈 파악',
      '고객 프로필에 적합한 상품 추천',
      '사업 연속성 및 가족 보호 관련 우려 해결',
      '고액 순자산 보유자와 신뢰 구축',
    ],
    clientPersona: sharedPersonas['lee-sangho-001'],
    agentId: 'agent_8901k9seba3kfzcrd4vnagpbjbfe',
    language: 'ko',
    successCriteria: [
      'Identified all major client concerns (inheritance tax, family protection, business continuity)',
      'Asked discovery questions about budget and financial situation',
      'Recommended 2-3 appropriate products with clear reasoning',
      'Explained product benefits in relation to client needs',
      'Proposed clear next steps',
    ],
    successCriteriaKo: [
      '모든 주요 고객 우려사항 파악 (상속세, 가족 보호, 사업 연속성)',
      '예산 및 재무 상황에 대한 발굴 질문 수행',
      '명확한 근거와 함께 2-3개 적합한 상품 추천',
      '고객 니즈와 연계하여 상품 혜택 설명',
      '명확한 다음 단계 제안',
    ],
  },
  'aia-2': {
    id: 'aia-korea-002',
    code: 'aia-2',
    title: 'Comparative Positioning - Second Meeting',
    titleKo: '경쟁사 비교 포지셔닝 - 2차 미팅',
    description:
      'Train salesperson to position AIA Universal Life vs. competing UL products during follow-up meeting. Client has reviewed multiple options (AIA, Samsung, Allianz, MetLife) and wants to understand differences.',
    descriptionKo:
      '후속 미팅에서 AIA 유니버셜 라이프와 경쟁사 UL 상품 비교 포지셔닝 훈련. 고객이 여러 옵션(AIA, 삼성, 알리안츠, 메트라이프)을 검토하고 차이점을 이해하고자 함.',
    difficulty: 'advanced',
    estimatedDuration: 15,
    learningObjectives: [
      'Position AIA Universal Life against competitor products with confidence',
      'Use data-driven comparisons (crediting rates, cost structures, flexibility)',
      'Handle objections about competitor products professionally',
      'Differentiate AIA based on long-term value and stability',
      'Close with competitive advantages that matter to client',
    ],
    learningObjectivesKo: [
      '경쟁사 상품 대비 AIA 유니버셜 라이프를 자신감 있게 포지셔닝',
      '데이터 기반 비교 활용 (공시이율, 비용 구조, 유연성)',
      '경쟁사 상품에 대한 이의 제기를 전문적으로 처리',
      '장기적 가치와 안정성을 바탕으로 AIA 차별화',
      '고객에게 중요한 경쟁 우위로 마무리',
    ],
    clientPersona: {
      ...sharedPersonas['lee-sangho-001'],
      // Override initial message for comparative scenario
      initialMessage:
        "Hello, it's Lee Sang-ho. Thanks for meeting with me again.",
      initialMessageKo:
        '안녕하세요, 다시 뵙게 되어 반갑습니다.',
    },
    agentId: 'agent_8901k9seba3kfzcrd4vnagpbjbfe', // Same agent, different scenario
    language: 'ko',
    successCriteria: [
      'Acknowledged client comparison concerns with empathy',
      'Highlighted AIA crediting rate stability with specific data (5-year average)',
      'Explained transparent cost structure vs. competitor variable charges',
      'Emphasized premium/withdrawal flexibility advantages',
      'Used AIA 100+ year track record as trust-building element',
      'Proposed visual comparison aids (table/chart) for next steps',
    ],
    successCriteriaKo: [
      '고객의 비교 우려사항을 공감하며 인정',
      '구체적 데이터로 AIA 공시이율 안정성 강조 (5년 평균)',
      '경쟁사의 변동 비용 대비 투명한 비용 구조 설명',
      '보험료/인출 유연성 장점 강조',
      'AIA 100년 이상의 실적을 신뢰 구축 요소로 활용',
      '다음 단계로 시각적 비교 자료(표/차트) 제안',
    ],
  },
};

// Helper function to get scenario by code
export function getScenarioByCode(code: string): InsuranceScenario | null {
  return insuranceScenarios[code] || null;
}
